import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MoreHorizontal, Zap, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

import { SEO } from "@/components/SEO";
import { TaskStatusSelect } from "@/components/TaskStatusDropdown";
import { TaskPrioritySelect } from "@/components/TaskPriorityDropdown";
import { TaskAssigneeSelect } from "@/components/TaskAssigneeDropdown";
import { TaskScopeSelect } from "@/components/TaskScopeSelect";
import { DueDateSelect } from "@/components/DueDateSelect";
import TaskLabels from "@/components/TaskDetails/TaskLabels";
import { STATUS_COLORS } from "@/lib/colorsMap";
import { getPriorityConfigForAPI } from "@/lib/priorityMap";
import TipTapEditor from "@/components/Editor/TiptapEditor";
import { AttachmentProvider, AttachmentUploadButton, AttachmentList, AttachmentDeleteModal } from "@/components/TaskDetails/TaskAttachments";
import { FiChevronRight, FiCheck } from 'react-icons/fi';
import StatusBotIcon from "@/components/StatusBotIcon";
import { TbCircle } from "react-icons/tb";
import { PiUserCircleDashed } from "react-icons/pi";

export function CreateTaskDialog({ open, onOpenChange, project, projects, onTaskCreated, initialData, generatingTask, createdFromSubmission }) {
    const { toast } = useToast();
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedProject, setSelectedProject] = useState(project);
    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
    const [morePopoverOpen, setMorePopoverOpen] = useState(false);
    const [isLoadingProjectDetails, setIsLoadingProjectDetails] = useState(false);
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const projectDropdownRef = useRef(null);
    const descriptionEditorRef = useRef(null);

    // Use selectedProject for all project-related logic
    const currentProject = selectedProject || project;

    // Reset form data and all state
    const resetForm = () => {
        setFormData({
            summary: '',
            description: '',
            status: 'todo',
            priority: 'no_priority',
            assigneeId: null,
            dueDate: null,
            labels: [],
            scope: 'tracked',
            releaseId: null
        });
        setUploadedFiles([]);
        setSelectedProject(null);
        setUsers([]);
        setProjectDropdownOpen(false);
        setMorePopoverOpen(false);
        setIsLoadingProjectDetails(false);
        if (descriptionEditorRef.current) {
            descriptionEditorRef.current.commands.clearContent();
        }
    };

    // Handle dialog close with unsaved changes check
    const handleClose = (force = false) => {
        if (!force && hasUnsavedChanges) {
            setShowUnsavedChangesDialog(true);
        } else {
            resetForm();
            onOpenChange(false);
        }
    };

    const confirmClose = () => {
        setShowUnsavedChangesDialog(false);
        resetForm();
        onOpenChange(false);
    };

    const cancelClose = () => {
        setShowUnsavedChangesDialog(false);
    };

    // Update selected project when project prop changes or dialog opens
    useEffect(() => {
        if (open && project) {
            setSelectedProject(project);
        } else if (open && !project && projects && projects.length === 1) {
            // Auto-select if there's only one project available
            setSelectedProject(projects[0]);
        } else if (!open) {
            // Reset everything when dialog closes
            resetForm();
        }
    }, [open, project]);

    // Also reset when dialog first opens to ensure clean state
    useEffect(() => {
        if (open && !initialData) {
            // Clear form for fresh start (unless we have initialData)
            setFormData({
                summary: '',
                description: '',
                status: 'todo',
                priority: 'no_priority',
                assigneeId: null,
                dueDate: null,
                labels: [],
                scope: 'tracked',
                releaseId: null
            });
            setUploadedFiles([]);
        }
    }, [open]);

    // Fetch full project details when project is selected
    useEffect(() => {
        const fetchFullProjectDetails = async () => {
            // Always fetch if we don't have statuses (even if we had them before for a different project)
            if (selectedProject && !selectedProject.statuses && selectedProject.organizationSlug && selectedProject.key) {
                setIsLoadingProjectDetails(true);
                try {
                    const response = await axiosInstance.get(`/projects/${selectedProject.organizationSlug}/${selectedProject.key}`);
                    const fullProjectData = response.data;
                    
                    // Update the selectedProject with full details
                    setSelectedProject(prev => ({
                        ...prev,
                        statuses: fullProjectData.statuses,
                        labels: fullProjectData.labels,
                        releases: fullProjectData.releases,
                        members: fullProjectData.members
                    }));

                    // Reset form data to use the new project's defaults (if not using initialData)
                    if (!initialData) {
                        const defaultStatus = fullProjectData.statuses?.[0]?.id || 'todo';
                        setFormData(prev => ({
                            ...prev,
                            status: defaultStatus,
                            labels: [],
                            releaseId: null,
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching full project details:', error);
                } finally {
                    setIsLoadingProjectDetails(false);
                }
            }
        };

        if (open && selectedProject) {
            fetchFullProjectDetails();
        }
    }, [selectedProject?.id, open, initialData, axiosInstance]);

    // Convert project statuses to the format expected by TaskStatusSelect
    const statuses = currentProject?.statuses?.reduce((acc, status) => ({
        ...acc,
        [status.id]: {
            name: status.name,
            icon: status.icon,
            color: STATUS_COLORS[status.color]?.text || `text-${status.color}-500`
        }
    }), {});

    const priorities = getPriorityConfigForAPI();

    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axiosInstance.get(`/organizations/${currentProject.organizationSlug}/users`);
                setUsers(response.data);
            } catch (error) {
                console.error('Error fetching users:', error);
                setUsers([]);
            }
        };

        if (open && currentProject?.organizationSlug) {
            fetchUsers();
        }
    }, [open, currentProject?.organizationSlug]);

    const [formData, setFormData] = useState({
        summary: initialData?.summary || '',
        description: initialData?.description || '',
        status: initialData?.status || 'todo',
        priority: initialData?.priority || 'no_priority',
        assigneeId: initialData?.assigneeId || null,
        dueDate: initialData?.dueDate || null,
        labels: initialData?.labels || [],
        scope: initialData?.scope || 'tracked',
        releaseId: initialData?.releaseId || null
    });

    // Check if secondary fields have non-default values
    const hasSecondaryValues = useMemo(() => {
        return formData.priority !== 'no_priority' || 
               formData.dueDate !== null || 
               (formData.labels && formData.labels.length > 0);
    }, [formData.priority, formData.dueDate, formData.labels]);

    // Check if user has entered any content
    const hasUnsavedChanges = useMemo(() => {
        // Check if summary has content
        if (formData.summary && formData.summary.trim().length > 0) {
            return true;
        }
        
        // Check if description has content beyond initial state
        if (formData.description && formData.description.content) {
            const hasContent = formData.description.content.some(node => {
                if (node.type === 'paragraph' && node.content) {
                    return node.content.some(item => item.text && item.text.trim().length > 0);
                }
                return node.type !== 'paragraph'; // Any non-paragraph content counts
            });
            if (hasContent) return true;
        }
        
        return false;
    }, [formData.summary, formData.description]);

    // Update formData when initialData changes
    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({
                ...prev,
                summary: initialData.summary || prev.summary,
                description: initialData.description || prev.description,
                priority: initialData.priority || prev.priority,
                assigneeId: initialData.assigneeId || prev.assigneeId,
                dueDate: initialData.dueDate || prev.dueDate,
                labels: initialData.labels || prev.labels,
                scope: initialData.scope || prev.scope,
                releaseId: initialData.releaseId || prev.releaseId
            }));
            
            // Set the TipTap JSON content directly (server converts markdown to TipTap JSON)
            if (initialData.description && descriptionEditorRef.current?.commands?.setContent) {
                descriptionEditorRef.current.commands.setContent(initialData.description);
            }

            // Handle submission files - show them as preloaded attachments
            if (initialData.submissionFiles && Array.isArray(initialData.submissionFiles)) {
                console.log('Loading submission files:', initialData.submissionFiles);
                setUploadedFiles(initialData.submissionFiles);
            }
        }
    }, [initialData]);

    const onFileUpload = async (file, editorUploadId, editorTempKey) => {
        try {
            const uploadId = editorUploadId || Math.random().toString(36).substring(7);
            const tempKey = editorTempKey || `temp_${Date.now()}_${Math.random().toString(36).substring(7)}`;
            // Create a temporary file object to store in state
            const tempFile = {
                id: uploadId,
                name: file.name,
                size: file.size,
                type: file.type,
                file: file,
                alt: file.name,
                uploadId: uploadId,
                tempKey,
                key: tempKey,
                url: URL.createObjectURL(file)
            };

            // Add to uploaded files state
            setUploadedFiles(prev => [...prev, tempFile]);

            // Return a temporary response that matches the expected format
            return {
                data: {
                    attachments: [{
                        url: tempFile.url,
                        filename: tempFile.name,
                        key: tempFile.key,
                        uploadId: uploadId,
                        tempKey: tempFile.tempKey
                    }]
                }
            };
        } catch (error) {
            console.error('Error handling file upload:', error);
            toast({
                description: "Failed to handle file upload",
                variant: "destructive"
            });
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Filter submission files that are still in uploadedFiles (not removed by user)
            const remainingSubmissionFiles = uploadedFiles
                .filter(file => file.fromSubmission)
                .map(file => ({
                    key: file.key,
                    filename: file.filename || file.name,
                    contentType: file.contentType || file.type
                }));

            // Prepare createdFromSubmission data with only remaining files
            const submissionData = createdFromSubmission ? {
                ...createdFromSubmission,
                filesToCopy: remainingSubmissionFiles // Only copy files that weren't removed
            } : null;

            // Determine scope based on explicit scope selection - default to tracked
            const scope = formData.scope || 'tracked';
            
            // First create the task
            const taskResponse = await axiosInstance.post(`/tasks/${currentProject?.organizationSlug}`, {
                ...formData,
                scope,
                releaseId: formData.releaseId || null,
                projectId: currentProject?.id,
                createdFromSubmission: submissionData
            });

            if (taskResponse && taskResponse.data) {
                const taskKey = taskResponse.data.key;

                // Filter out submission files - they are already copied on the server side
                // Only upload files that have an actual File object and are not from submission
                const filesToUpload = uploadedFiles.filter(file => file.file && !file.fromSubmission);

                // Upload only newly added files (not submission files)
                let uploadFailures = [];
                if (filesToUpload.length > 0) {
                    const uploadFormData = new FormData();
                    filesToUpload.forEach(f => uploadFormData.append('file', f.file));
                    uploadFormData.append('uploadIds', JSON.stringify(filesToUpload.map(f => f.uploadId)));
                    uploadFormData.append('tempKeys', JSON.stringify(filesToUpload.map(f => f.tempKey || f.key)));
                    const uploadResponse = await axiosInstance.post(
                        `/tasks/${currentProject?.organizationSlug}/${taskKey}/attachments`,
                        uploadFormData
                    );
                    uploadFailures = uploadResponse.data?.failedUploads || [];
                }

                if (uploadFailures.length > 0) {
                    toast({
                        description: (
                            <div>
                                <button
                                    onClick={() => navigate(`/${currentProject?.organizationSlug}/plan/${taskKey}`)}
                                    className="text-primary underline hover:no-underline font-medium bg-transparent border-none cursor-pointer p-0 text-left"
                                >
                                    {taskKey} created
                                </button>
                                <Collapsible className="mt-1">
                                    <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium cursor-pointer">
                                        {uploadFailures.length} file{uploadFailures.length > 1 ? 's' : ''} failed to upload
                                        <ChevronDown className="h-3 w-3" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-1 text-xs opacity-80">
                                        {uploadFailures.map((f, i) => <div key={i}>{f.filename}</div>)}
                                    </CollapsibleContent>
                                </Collapsible>
                            </div>
                        ),
                    });
                } else {
                    toast({
                        description: (
                            <button 
                                onClick={() => navigate(`/${currentProject?.organizationSlug}/plan/${taskKey}`)}
                                className="text-primary underline hover:no-underline font-medium bg-transparent border-none cursor-pointer p-0 text-left"
                            >
                                {taskKey} created successfully
                            </button>
                        ),
                    });
                }

                if (onTaskCreated) onTaskCreated({
                    id: taskResponse.data._id,
                    key: taskResponse.data.key
                });

                uploadedFiles.forEach(file => {
                    if (file.url && file.url.startsWith('blob:')) {
                        URL.revokeObjectURL(file.url);
                    }
                });

                // Reset and close
                resetForm();
                onOpenChange(false);
            }
        } catch (error) {
            console.error('Error creating task:', error);
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to create task",
                variant: "destructive"
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
                setProjectDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Close dropdown when dialog closes
    useEffect(() => {
        if (!open) {
            setProjectDropdownOpen(false);
        }
    }, [open]);

    // Remove the separate submission/other files logic - just use all uploadedFiles
    const handleTaskAttachmentsChange = (updatedTask) => {
        setUploadedFiles(updatedTask.attachments);
    };

    return (
        <>
        <Dialog open={open} onOpenChange={handleClose}>
            {open && currentProject &&
                <SEO title={`${currentProject.name} › New task`} />
            }
            
            <DialogContent className="max-w-md md:max-w-3xl grid grid-cols-1">
                {generatingTask && (
                    <div className="absolute inset-0 z-[100] flex items-center justify-center w-full h-full bg-background/70 backdrop-blur-sm rounded-lg p-8 pointer-events-auto">
                        <div className="text-center flex items-center gap-3">
                            <StatusBotIcon className="h-6 w-6 text-teal-500" />
                            <div className="relative inline-block bg-radial from-black/40 from-40% to-black/80 to-60% dark:from-white/40 dark:from-40% dark:to-white/80 dark:to-60% bg-clip-text text-transparent animate-shimmer text-sm">
                                StatusBot is generating your task
                            </div>
                        </div>
                    </div>
                )}
                <div className="relative w-full h-full">
                    <DialogHeader className="pb-3">
                        <DialogTitle className="flex items-center gap-2">
                            {projects && projects.length > 0 ? (
                                <div className="relative" ref={projectDropdownRef}>
                                    <Button 
                                        variant="secondary"
                                        className="h-8 px-3 text-sm font-medium w-auto flex items-center gap-1.5 hover:bg-secondary/80 transition-colors"
                                        onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                                    >
                                        <span>{currentProject?.name || "Select project"}</span>
                                    </Button>
                                    
                                    {projectDropdownOpen && (
                                        <div className="absolute top-full left-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50 min-w-max animate-in fade-in-0 zoom-in-95 duration-200">
                                            <div className="p-1">
                                                {projects.map((project) => (
                                                    <button
                                                        key={project.id}
                                                        className="w-full text-left px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors flex items-center justify-between gap-3"
                                                        onClick={() => {
                                                            setSelectedProject(project);
                                                            setProjectDropdownOpen(false);
                                                        }}
                                                    >
                                                        <span className="whitespace-nowrap">{project.name}</span>
                                                        {selectedProject?.id === project.id && (
                                                            <FiCheck className="w-3 h-3 text-primary" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Button 
                                    variant="secondary"
                                    disabled
                                    className="h-8 px-3 text-sm font-medium w-auto"
                                >
                                    {currentProject?.name || "Project"}
                                </Button>
                            )}
                            <FiChevronRight 
                                className={`w-3 h-3 transition-transform duration-200 ${
                                    projectDropdownOpen ? 'rotate-90' : ''
                                }`} 
                            />
                            <span className="text-base font-normal text-neutral-600 dark:text-neutral-400">New task</span>
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit}>
                        <AttachmentProvider 
                            task={{ attachments: uploadedFiles }} 
                            onTaskChange={handleTaskAttachmentsChange} 
                            descriptionEditorRef={descriptionEditorRef} 
                            newTask={true}
                        >
                            <Input
                                id="summary"
                                value={formData.summary}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    summary: e.target.value
                                }))}
                                required
                                placeholder="Task summary"
                                className="placeholder:text-neutral-500 dark:placeholder:text-neutral-600 text-lg font-semibold focus:outline-none py-2 min-h-11 border-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent p-0"
                            />
                            {/* Scrollable div for the editor */}
                            <div 
                                className="overflow-y-auto"
                                style={{
                                    maxHeight: `calc(90vh - ${255 + (uploadedFiles.length * 28)}px)`
                                }}
                            >
                                <TipTapEditor
                                    id="description"
                                    content={formData.description}
                                    placeholder="Add description..."
                                    className="min-h-24"
                                    withMentions
                                    withCodeBlocks
                                    withBubbleMenu
                                    withS3Images
                                    withS3Uploads
                                    organizationSlug={currentProject?.organizationSlug}
                                    onFileUpload={onFileUpload}
                                    onUpdate={({ editor }) => {
                                        const json = editor.getJSON();
                                        setFormData(prev => ({
                                            ...prev,
                                            description: json
                                        }));
                                    }}
                                    ref={descriptionEditorRef}
                                    onEditorReady={(editor) => {
                                        descriptionEditorRef.current = editor;
                                        // Set initial description content when editor becomes ready
                                        // This handles the case where initialData arrives before the editor is ready
                                        if (initialData?.description) {
                                            editor.commands.setContent(initialData.description);
                                        }
                                    }}
                                />
                            </div>

                            {/* Attachment list between description and task settings */}
                            <div className="mt-4 mb-4 pt-4">
                                <AttachmentList style="minimal" />
                            </div>

                            {/* Primary fields: Status, Scope, Assignee, and More */}
                            <div className="flex flex-wrap items-center gap-2">
                                {isLoadingProjectDetails ? (
                                    <Skeleton className="h-7 w-24" />
                                ) : !currentProject?.statuses ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled
                                        className="h-7 px-1.5 -ml-1.5 text-sm opacity-30 cursor-not-allowed gap-2"
                                    >
                                        <TbCircle className="w-5 h-5 text-blue-500" />
                                        To Do
                                    </Button>
                                ) : (
                                    statuses &&
                                    <TaskStatusSelect
                                        statuses={statuses}
                                        value={formData.status}
                                        onChange={(status) => setFormData(prev => ({
                                            ...prev,
                                            status
                                        }))}
                                    />
                                )}
                                {isLoadingProjectDetails ? (
                                    <Skeleton className="h-7 w-28" />
                                ) : !currentProject?.statuses ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled
                                        className="h-7 px-1.5 -ml-1.5 text-sm opacity-30 cursor-not-allowed gap-2"
                                    >
                                        <Zap className="w-[18px] h-[18px] text-emerald-600" />
                                        Active Week
                                    </Button>
                                ) : (
                                    <TaskScopeSelect
                                        value={formData.scope}
                                        releases={currentProject?.releases || []}
                                        selectedReleaseId={formData.releaseId}
                                        onChange={({ scope, releaseId }) => setFormData(prev => ({
                                            ...prev,
                                            scope,
                                            releaseId
                                        }))}
                                    />
                                )}
                                {isLoadingProjectDetails ? (
                                    <Skeleton className="h-7 w-32" />
                                ) : !users.length ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled
                                        className="h-7 px-1.5 -ml-1.5 text-sm opacity-30 cursor-not-allowed gap-2"
                                    >
                                        <PiUserCircleDashed className="h-5 w-5 text-neutral-500" />
                                        Unassigned
                                    </Button>
                                ) : (
                                    users &&
                                    <TaskAssigneeSelect
                                        users={users}
                                        value={formData.assigneeId}
                                        onChange={(assigneeId) => setFormData(prev => ({
                                            ...prev,
                                            assigneeId
                                        }))}
                                        showName
                                    />
                                )}
                                
                                {/* Vertical separator */}
                                <div className="h-5 w-px bg-border mx-1" />
                                
                                {/* More options popover */}
                                <Popover open={morePopoverOpen} onOpenChange={setMorePopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            type="button"
                                            disabled={!currentProject?.statuses}
                                            className="h-7 w-7 p-0 flex items-center justify-center relative disabled:opacity-30"
                                        >
                                            <MoreHorizontal className="w-4 h-4" />
                                            {hasSecondaryValues && (
                                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full" />
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[280px] p-3" align="start" side="top">
                                        <div className="space-y-4">
                                            <div>
                                                <div className="text-xs font-medium text-muted-foreground mb-2 px-1">Priority</div>
                                                {priorities &&
                                                    <TaskPrioritySelect
                                                        priorities={priorities}
                                                        value={formData.priority}
                                                        onChange={(priority) => setFormData(prev => ({
                                                            ...prev,
                                                            priority
                                                        }))}
                                                    />
                                                }
                                            </div>
                                            
                                            <div>
                                                <div className="text-xs font-medium text-muted-foreground mb-2 px-1">Due Date</div>
                                                <DueDateSelect
                                                    task={formData}
                                                    showLabel={false}
                                                    newTask={true}
                                                    organizationSlug={currentProject?.organizationSlug}
                                                    onTaskChange={(updatedTask) => setFormData(prev => ({
                                                        ...prev,
                                                        dueDate: updatedTask.dueDate
                                                    }))}
                                                />
                                            </div>
                                            
                                            <div>
                                                <div className="text-xs font-medium text-muted-foreground mb-2 px-1">Labels</div>
                                                {isLoadingProjectDetails ? (
                                                    <Skeleton className="h-7 w-full" />
                                                ) : !currentProject?.labels ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        disabled
                                                        className="h-7 px-2 text-sm opacity-30 cursor-not-allowed w-full justify-start"
                                                    >
                                                        No labels
                                                    </Button>
                                                ) : (
                                                    <TaskLabels
                                                        task={formData}
                                                        showLabel={false}
                                                        newTask={true}
                                                        newTaskAvailableLabels={currentProject?.labels || []}
                                                        organizationSlug={currentProject?.organizationSlug}
                                                        onTaskChange={(updatedTask) => setFormData(prev => ({
                                                            ...prev,
                                                            labels: updatedTask.labels
                                                        }))}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            <DialogFooter className="border-t border-neutral-200 dark:border-neutral-800 pt-5 mt-5 flex flex-row justify-between sm:justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <AttachmentUploadButton />
                                </div>
                                <div className="flex flex-col md:flex-row md:justify-between space-x-4">
                                    <Button type="submit" disabled={submitting} loading={submitting}>Create task</Button>
                                </div>
                            </DialogFooter>

                            <AttachmentDeleteModal />
                        </AttachmentProvider>
                    </form>
                </div>
            </DialogContent>
        </Dialog>

        {/* Unsaved Changes Confirmation Dialog */}
        <Dialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Unsaved changes</DialogTitle>
                    <DialogDescription>
                        You have unsaved changes. Are you sure you want to close?
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={cancelClose}>
                        Continue editing
                    </Button>
                    <Button variant="destructive" onClick={confirmClose}>
                        Discard changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}